"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCopy, IconExternalLink, IconRosetteDiscountCheck, IconX, IconZoomCode } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { tokenizeJson, type JsonToken } from "@/components/domain/registration/json-highlight";
import { Favicon } from "@/components/icons/favicon";
import { Button } from "@domainstack/ui/button";
import { Checkbox } from "@domainstack/ui/checkbox";
import { CopyButton } from "@domainstack/ui/copy-button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@domainstack/ui/dialog";
import { ResponsiveTooltip, ResponsiveTooltipContent, ResponsiveTooltipTrigger } from "@domainstack/ui/responsive-tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
import { cn } from "@domainstack/ui/utils";
function withTokenKeys(tokens: JsonToken[]) {
  const seen = new Map<string, number>();
  return tokens.map(token => {
    const baseKey = `${token.type}:${token.value}`;
    const duplicateCount = seen.get(baseKey) ?? 0;
    seen.set(baseKey, duplicateCount + 1);
    return {
      key: `${baseKey}:${duplicateCount}`,
      token
    };
  });
}
function HighlightedLine(t0) {
  const $ = _c(6);
  const {
    line,
    tokens
  } = t0;
  if (!tokens || tokens.length === 0) {
    const t1 = line || "\xA0";
    let t2;
    if ($[0] !== t1) {
      t2 = <>{t1}</>;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t1;
  if ($[2] !== tokens) {
    t1 = tokens.map(_temp);
    $[2] = tokens;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <>{t1}</>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function _temp(t0) {
  const {
    key,
    token
  } = t0;
  return <span key={key} className={cn(token.type === "key" && "text-blue-700 dark:text-blue-400", token.type === "string" && "text-emerald-700 dark:text-emerald-400", token.type === "number" && "text-amber-700 dark:text-amber-400", token.type === "boolean" && "text-violet-700 dark:text-violet-400", token.type === "null" && "text-stone-500 italic dark:text-stone-400")}>{token.value}</span>;
}
interface RawDataDialogProps {
  domain: string;
  format: string;
  /** Raw data: JSON object for RDAP, plain text string for WHOIS. Undefined until loaded. */
  data?: Record<string, unknown> | string;
  /** Shown in place of the data while it is not available. */
  loadState?: "loading" | "error";
  /** Replaces the default loading message; should fill the code pane like the loaded data. */
  loadingFallback?: React.ReactNode;
  /** Called whenever the dialog opens or closes. */
  onOpenChange?: (open: boolean) => void;
  serverName: string;
  serverUrl: string | undefined;
}
export function RawDataDialog(t0) {
  const $ = _c(78);
  const {
    domain,
    format,
    data,
    loadState,
    loadingFallback,
    onOpenChange,
    serverName,
    serverUrl
  } = t0;
  const [open, setOpen] = useState(false);
  const [wrapLines, setWrapLines] = useState(true);
  let t1;
  if ($[0] !== onOpenChange) {
    t1 = next => {
      setOpen(next);
      onOpenChange?.(next);
    };
    $[0] = onOpenChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleOpenChange = t1;
  let t2;
  bb0: {
    if (data === undefined) {
      t2 = "";
      break bb0;
    }
    if (typeof data === "string") {
      t2 = data;
      break bb0;
    }
    let t3;
    if ($[2] !== data) {
      t3 = JSON.stringify(data, null, 2);
      $[2] = data;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = t3;
  }
  const formattedData = t2;
  let t3;
  if ($[4] !== data || $[5] !== formattedData) {
    bb1: {
      if (data === undefined) {
        let t4;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = [];
          $[7] = t4;
        } else {
          t4 = $[7];
        }
        t3 = t4;
        break bb1;
      }
      const source = formattedData.trim();
      if (typeof data === "string") {
        t3 = source.split("\n").map(_temp2);
        break bb1;
      }
      t3 = tokenizeJson(source).map(_temp4);
    }
    $[4] = data;
    $[5] = formattedData;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const lineItems = t3;
  const t4 = `View raw ${format} data`;
  let t5;
  if ($[8] !== handleOpenChange) {
    t5 = () => handleOpenChange(true);
    $[8] = handleOpenChange;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IconZoomCode className="size-4 text-foreground/95" />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== format) {
    t7 = <span className="sr-only">View raw {format} data</span>;
    $[11] = format;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t7) {
    t8 = <TooltipTrigger render={<Button variant="ghost" size="icon-sm" aria-label={t4} onClick={t5}>{t6}{t7}</Button>} />;
    $[13] = t4;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== format) {
    t9 = <TooltipContent><p>View raw {format} data</p></TooltipContent>;
    $[17] = format;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <DialogTrigger render={<Tooltip>{t8}{t9}</Tooltip>} />;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  let t12;
  if ($[22] !== domain) {
    t11 = <Favicon domain={domain} />;
    t12 = <span className="truncate text-base tracking-[-0.01em] lowercase">{domain}</span>;
    $[22] = domain;
    $[23] = t11;
    $[24] = t12;
  } else {
    t11 = $[23];
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <IconRosetteDiscountCheck className="size-3.5 text-accent-green" />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== format) {
    t14 = <ResponsiveTooltipTrigger render={<span className="ml-1 flex cursor-default items-center gap-1 text-[13px] font-normal text-foreground/75">{t13}<span>{format}</span></span>} />;
    $[26] = format;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== serverName || $[29] !== serverUrl) {
    t15 = serverUrl ? <a href={serverUrl} target="_blank" rel="noopener" className="flex items-center gap-1 underline underline-offset-2">{serverName}<IconExternalLink className="size-3 -translate-y-[1px]" /></a> : serverName;
    $[28] = serverName;
    $[29] = serverUrl;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15) {
    t16 = <ResponsiveTooltipContent><span className="flex items-center gap-1 truncate">Verified by{" "}<span className="font-medium">{t15}</span></span></ResponsiveTooltipContent>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t14 || $[34] !== t16) {
    t17 = <ResponsiveTooltip>{t14}{t16}</ResponsiveTooltip>;
    $[33] = t14;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t17) {
    t18 = <DialogHeader className="place-items-start space-y-1 border-b border-border bg-card/60 p-4"><DialogTitle className="flex items-center gap-2">{t11}{t12}{t17}</DialogTitle></DialogHeader>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = `Raw ${format} data`;
  let t20;
  if ($[40] !== data || $[41] !== lineItems || $[42] !== loadState || $[43] !== loadingFallback || $[44] !== wrapLines) {
    t20 = data === undefined ? loadState === "loading" && loadingFallback ? loadingFallback : <p className="flex h-full items-center justify-center p-6 text-center text-[13px] text-muted-foreground" role="status">{loadState === "loading" ? "Loading\u2026" : "Raw data isn\u2019t available right now."}</p> : <div className="p-3"><pre className="font-mono text-xs leading-5 text-foreground/90"><code className={cn("grid", wrapLines ? "grid-cols-[auto_1fr]" : "w-max min-w-full grid-cols-[auto_auto]")}>{lineItems.map(item => <div key={item.lineNumber} className="col-span-2 grid grid-cols-subgrid rounded px-1 py-0.5 hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none active:bg-muted/50"><span className="justify-self-end px-1 text-muted-foreground/70 select-none">{item.lineNumber}</span><span className={cn("min-w-0 pr-1 pl-3", wrapLines ? "break-all whitespace-pre-wrap" : "whitespace-pre")}><HighlightedLine line={item.line} tokens={item.tokens} /></span></div>)}</code></pre></div>;
    $[40] = data;
    $[41] = lineItems;
    $[42] = loadState;
    $[43] = loadingFallback;
    $[44] = wrapLines;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t19 || $[47] !== t20) {
    t21 = <div tabIndex={0} className="min-h-0 flex-1 overflow-auto overscroll-contain bg-popover/10 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1" aria-label={t19}>{t20}</div>;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = data === undefined;
  let t23;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setWrapLines(_temp5);
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== wrapLines) {
    t24 = <Checkbox checked={wrapLines} className="size-3.5" />;
    $[50] = wrapLines;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t22 || $[53] !== t24) {
    t25 = <Button variant="outline" size="sm" className="gap-2 !px-3 text-[13px]" disabled={t22} onClick={t23}>{t24}Wrap lines</Button>;
    $[52] = t22;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== data || $[56] !== formattedData) {
    t26 = data === undefined ? <Button variant="outline" size="sm" className="gap-2 !px-3 text-[13px]" disabled={true}><IconCopy />Copy</Button> : <CopyButton variant="outline" size="sm" className="gap-2 !px-3 text-[13px]" value={formattedData} showLabel={true} />;
    $[55] = data;
    $[56] = formattedData;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== handleOpenChange) {
    t27 = () => handleOpenChange(false);
    $[58] = handleOpenChange;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <IconX />;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t27) {
    t29 = <Button variant="outline" size="sm" onClick={t27} className="gap-2 !px-3 text-[13px]">{t28}Close</Button>;
    $[61] = t27;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t26 || $[64] !== t29) {
    t30 = <div className="space-x-2">{t26}{t29}</div>;
    $[63] = t26;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t25 || $[67] !== t30) {
    t31 = <div className="flex w-full items-center justify-between gap-2 border-t border-border bg-card/60 p-3">{t25}{t30}</div>;
    $[66] = t25;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t18 || $[70] !== t21 || $[71] !== t31) {
    t32 = <DialogContent className="h-[min(100%,48rem)] gap-0 p-0 sm:max-w-2xl">{t18}{t21}{t31}</DialogContent>;
    $[69] = t18;
    $[70] = t21;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== handleOpenChange || $[74] !== open || $[75] !== t10 || $[76] !== t32) {
    t33 = <Dialog open={open} onOpenChange={handleOpenChange}>{t10}{t32}</Dialog>;
    $[73] = handleOpenChange;
    $[74] = open;
    $[75] = t10;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  return t33;
}
function _temp5(prev) {
  return !prev;
}
function _temp4(tokens, index_0) {
  return {
    lineNumber: index_0 + 1,
    line: tokens.map(_temp3).join(""),
    tokens: withTokenKeys(tokens)
  };
}
function _temp3(token) {
  return token.value;
}
function _temp2(line, index) {
  return {
    lineNumber: index + 1,
    line,
    tokens: null
  };
}